/**
 * pdfTransform — the explicit world(mm) → page(pt) transform contract for the
 * vector layout-plot PDF exporter (PDF-1).
 *
 * Implements `PLAN-plot-pdf.md` §4 EXACTLY, in this strict order:
 *
 *   1. **Near-origin localize** — subtract the plot-region origin. PDF numeric
 *      content uses single-precision floats; large UTM/survey coordinates jitter
 *      unless everything is made relative to the region origin first.
 *   2. **Plot scale** — Layout area = 1:1; otherwise numerator/denominator. This
 *      wraps both paper-space and (later, PDF-3) per-VP output.
 *   3. **Rotation** — 0 / 90 / 180 / 270 (DXF code 73), about the printable
 *      centre.
 *   4. **Offset + center** — DXF codes 46/47 shift the content inside the
 *      printable area; the PlotCentered flag re-centres it.
 *   5. **Printable origin** — paper lower-left + (left, bottom) margins.
 *   6. **mm → pt** — multiply by 72/25.4. **NO Y-flip**: PDF and CAD model are
 *      both Y-up, so a model point with larger Y maps to a page point with
 *      larger Y. (A Y-flip would only be needed if we routed through SVG /
 *      browser coordinates, which we deliberately do not.)
 *
 * The function returns a closure `(x, y) -> [pageXpt, pageYpt]` so callers can
 * map every primitive vertex through one cached affine.
 *
 * Color + lineweight plot semantics live here too (they are part of the
 * world→page mapping for stroke style, not geometry):
 *   - lineweight mm → pt (×72/25.4) with a hairline clamp for lw 0;
 *   - ACI → rgb reusing the scene ACI table + resolver, with the plot tweak
 *     "ACI 7 → black, white-fallback → black" so drawings read on white paper.
 */

import {
  PlotAreaType,
  PlotRotation,
  plotScalesLineweights,
  printableAreaMm,
  type LayoutPlotSettings,
} from "@dxf-ai-editor/drawing-model";

import { aciToHex } from "../../scene/aciColorMap";
import { hexToRgb, type RgbColor } from "../../color/rgbCodec";
import {
  resolveColorHex,
  type LayerColorMap,
} from "../../scene3d/rendering/colorResolution";

/** mm → PostScript point conversion factor (1 pt = 1/72 inch, 1 inch = 25.4 mm). */
export const MM_TO_PT = 72 / 25.4;

/**
 * Minimum stroke width in points for a "hairline" (DXF lineweight 0). PDF lw 0
 * means "thinnest renderable line" which is device-dependent and often
 * invisible at high DPI, so we clamp to a small positive value (~0.1 pt ≈ the
 * AutoCAD plotter hairline). Matches `PLAN-plot-pdf.md` §3 / Appendix D.
 */
export const HAIRLINE_PT = 0.1;

// `RgbColor` (0..1, pdf-lib `setStrokingRgbColor` convention) is the neutral
// `color/rgbCodec` one-truth, shared with the scene pipe. Re-exported so pdf
// consumers keep their `import { RgbColor } from "…/pdfTransform"` path.
export type { RgbColor } from "../../color/rgbCodec";

/** Black — the plot fallback on white paper. */
export const PLOT_BLACK: RgbColor = { r: 0, g: 0, b: 0 };

/**
 * White — the paper background colour. A WIPEOUT mask (PDF-5d) fills its
 * polygon with this so it overwrites (hides) geometry painted beneath it. NOTE
 * the plot-colour resolvers tweak a WHITE *entity* colour → black (so white
 * geometry reads on white paper); the wipeout mask deliberately bypasses that
 * tweak — its purpose IS to paint the page background.
 */
export const PLOT_WHITE: RgbColor = { r: 1, g: 1, b: 1 };

/** A point mapped from world mm to page pt. */
export type PagePoint = readonly [number, number];

/** The mapping closure built by {@link buildPdfTransform}. */
export type PdfTransform = (x: number, y: number) => PagePoint;

/**
 * Inputs for the transform. `regionOrigin` is the lower-left of the plotted
 * region in world (paper-space mm) coordinates — for a Layout-area plot this
 * is the lower-left of the printable rectangle expressed in paper space
 * (typically the paper origin, i.e. 0,0, but can differ for templates whose
 * paper-space geometry sits at a non-zero offset).
 */
export interface PdfTransformParams {
  settings: LayoutPlotSettings;
  /**
   * Lower-left of the plotted region in world (paper-space mm). Subtracted
   * first (§4.1 near-origin localize). Defaults to (0, 0).
   */
  regionOrigin?: { x: number; y: number };
  /**
   * Explicit plot-scale factor (world-mm → paper-mm), used INSTEAD of
   * {@link resolvePlotScale} when provided. PDF-4 supplies this for Window /
   * Extents "fit" plots (scale = min(printableW/regionW, printableH/regionH))
   * and for an explicit standard scale resolved by the plot-area resolver.
   * Omitting it preserves the prior behaviour exactly (Layout = 1:1, else
   * numerator/denominator).
   */
  scaleOverride?: number;
  /**
   * Extra centering offset in mm, ADDED alongside `settings.offset` at step 4
   * (offset + center). PDF-4 supplies this so a fit Window / Extents plot is
   * centred inside the printable area (matching AutoCAD's Window+Fit / the
   * PlotCentered flag). Defaults to (0, 0) so existing callers are unchanged.
   */
  centeringOffsetMm?: { x: number; y: number };
}

/**
 * Resolve the effective plot scale factor (world-mm → paper-mm). Layout area
 * is always 1:1 (paper-space is authored in mm at 1:1 to the sheet). Other
 * areas honour the numerator/denominator ratio.
 */
export function resolvePlotScale(settings: LayoutPlotSettings): number {
  if (settings.plotAreaType === PlotAreaType.Layout) return 1;
  const { numerator, denominator } = settings.scale;
  if (
    !Number.isFinite(numerator) ||
    !Number.isFinite(denominator) ||
    denominator === 0
  ) {
    return 1;
  }
  return numerator / denominator;
}

/** Quarter-turn angle in radians for a {@link PlotRotation}. */
function rotationRadians(rotation: PlotRotation): number {
  switch (rotation) {
    case PlotRotation.Quarter:
      return Math.PI / 2;
    case PlotRotation.Half:
      return Math.PI;
    case PlotRotation.ThreeQuarter:
      return (3 * Math.PI) / 2;
    case PlotRotation.None:
    default:
      return 0;
  }
}

/**
 * Build the world(mm) → page(pt) mapping closure per §4.
 *
 * The closure performs, for each (x, y):
 *   localized   = (x, y) − regionOrigin                       (step 1)
 *   scaled      = localized × plotScale                       (step 2)
 *   rotated     = R(rotation) · (scaled − printableCenterMm)  (step 3, about centre)
 *                 + printableCenterMm
 *   offset      = rotated + (offsetX, offsetY) [+ centering]  (step 4)
 *   paperMm     = offset + (marginLeft, marginBottom)         (step 5, printable origin)
 *   pagePt      = paperMm × (72/25.4)                         (step 6, NO Y-flip)
 *
 * Rotation is taken about the printable-area centre so a 90°/180°/270° plot
 * stays inside the sheet rather than swinging off the corner.
 */
export function buildPdfTransform(params: PdfTransformParams): PdfTransform {
  const { settings } = params;
  const regionOrigin = params.regionOrigin ?? { x: 0, y: 0 };
  // PDF-4: a fit/standard scale override (Window/Extents) replaces the
  // settings-derived scale; omitting it preserves the prior behaviour exactly.
  const scale =
    params.scaleOverride !== undefined && Number.isFinite(params.scaleOverride)
      ? params.scaleOverride
      : resolvePlotScale(settings);
  const centering = params.centeringOffsetMm ?? { x: 0, y: 0 };
  const angle = rotationRadians(settings.rotation);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  const printable = printableAreaMm(settings);
  // Printable centre in printable-local mm (origin at printable lower-left).
  const cx = printable.width / 2;
  const cy = printable.height / 2;

  const marginLeft = settings.margins.left;
  const marginBottom = settings.margins.bottom;
  // Plot offset (46/47) plus the PDF-4 centering offset (both in mm).
  const offsetX = settings.offset.x + centering.x;
  const offsetY = settings.offset.y + centering.y;

  return (x: number, y: number): PagePoint => {
    // (1) near-origin localize
    const lx = x - regionOrigin.x;
    const ly = y - regionOrigin.y;
    // (2) plot scale
    const sx = lx * scale;
    const sy = ly * scale;
    // (3) rotation about printable centre
    const dx = sx - cx;
    const dy = sy - cy;
    const rx = dx * cos - dy * sin + cx;
    const ry = dx * sin + dy * cos + cy;
    // (4) offset (+centering is folded into offset by the caller when the
    //     PlotCentered flag is set — see resolveCenteringOffset)
    const ox = rx + offsetX;
    const oy = ry + offsetY;
    // (5) printable origin = paper lower-left + margins
    const paperMmX = ox + marginLeft;
    const paperMmY = oy + marginBottom;
    // (6) mm → pt, NO Y-flip
    return [paperMmX * MM_TO_PT, paperMmY * MM_TO_PT];
  };
}

/**
 * Resolve a lineweight (in mm, already decoded from DXF code 370) to a PDF
 * stroke width in points, honouring the "scale lineweights" flag.
 *
 * `lineweightMm` is the entity's resolved lineweight in millimetres
 * (`adapterContext.parseLineweight` produces this; ByLayer/ByBlock/Default
 * are resolved upstream to a concrete mm value). A value of 0 means "hairline"
 * and is clamped to {@link HAIRLINE_PT}.
 */
export function lineweightToPt(
  lineweightMm: number,
  settings: LayoutPlotSettings,
): number {
  const scaled = plotScalesLineweights(settings)
    ? lineweightMm * resolvePlotScale(settings)
    : lineweightMm;
  const pt = scaled * MM_TO_PT;
  if (!Number.isFinite(pt) || pt <= 0) return HAIRLINE_PT;
  return Math.max(pt, HAIRLINE_PT);
}

/**
 * Decode the DXF lineweight special codes to a concrete mm value for plotting.
 *   −1 = ByLayer → resolved via `layerLineweightMm` (caller supplies the layer
 *        record's lineweight); falls back to Default when the layer is also −1.
 *   −2 = ByBlock → `blockLineweightMm` (parent INSERT lineweight) or Default.
 *   −3 = Default → 0.25 mm (LWDEFAULT).
 *    0 = hairline.
 * Any positive value is already mm.
 *
 * Codes are the raw integer from group 370 (mm × 100) OR an already-decoded mm
 * value; we detect via magnitude the same way `adapterContext.parseLineweight`
 * does (values > 5 are treated as 0.01 mm units).
 */
export function resolveLineweightMm(
  raw: number,
  options: {
    layerLineweightMm?: number;
    blockLineweightMm?: number;
  } = {},
): number {
  const DEFAULT_MM = 0.25; // LWDEFAULT
  if (raw === -1) {
    // ByLayer
    const layer = options.layerLineweightMm;
    if (layer === undefined) return DEFAULT_MM;
    return layer < 0 ? DEFAULT_MM : layer;
  }
  if (raw === -2) {
    // ByBlock
    const block = options.blockLineweightMm;
    if (block === undefined) return DEFAULT_MM;
    return block < 0 ? DEFAULT_MM : block;
  }
  if (raw === -3) return DEFAULT_MM; // Default
  if (raw === 0) return 0; // hairline
  if (!Number.isFinite(raw)) return DEFAULT_MM;
  // Group-code 370 values are in 0.01 mm units (0..211). Larger-magnitude
  // values are already mm (mirrors adapterContext.parseLineweight).
  const mm = Math.abs(raw) > 5 ? raw * 0.01 : raw;
  return mm;
}

// `hexToRgb` (`#rrggbb` → RgbColor 0..1, black on malformed) is the neutral
// `color/rgbCodec` one-truth, imported above for local use and re-exported so
// pdf consumers (`hatchConverter`, …) keep their `import { hexToRgb } from
// "…/pdfTransform"` path. (The prior local copy fell back to `PLOT_BLACK`,
// which is `{r:0,g:0,b:0}` — identical to the neutral codec's black fallback.)
export { hexToRgb } from "../../color/rgbCodec";

/** Whether a hex colour is (near-)white — the plot fallback target. */
function isWhite(hex: string): boolean {
  return hex.trim().toLowerCase() === "#ffffff";
}

/**
 * Convert an already-resolved `#rrggbb` hex colour into a plot {@link RgbColor},
 * applying the same plot tweak as {@link resolvePlotRgb}: any white result
 * becomes black so the geometry reads on the white PDF page. Used by the
 * DIMENSION / MULTILEADER converters (PDF-5b), whose scene adapters resolve the
 * BYLAYER/BYBLOCK chain to a concrete `colorHex` per sub-primitive — so the IR
 * only needs to apply the white→black tweak, not re-walk the chain.
 */
export function hexToPlotRgb(hex: string): RgbColor {
  if (isWhite(hex)) return PLOT_BLACK;
  return hexToRgb(hex);
}

/**
 * Resolve an ACI index directly to a plot RGB. Plot tweak: ACI 7 (screen white)
 * → black on white paper. ByBlock(0)/ByLayer(256)/out-of-range → black fallback.
 */
export function aciToPlotRgb(aci: number): RgbColor {
  if (aci === 7) return PLOT_BLACK;
  const hex = aciToHex(aci);
  if (!hex || isWhite(hex)) return PLOT_BLACK;
  return hexToRgb(hex);
}

/**
 * Resolve an entity's plot colour through the full BYLAYER/BYBLOCK chain
 * (reusing the scene `resolveColorHex`), then apply the plot tweak: any white
 * result becomes black so the geometry is visible on the white PDF page.
 *
 * @param entityColor Raw color value from the entity (ACI text, "BYLAYER", hex…).
 * @param layerName   Entity layer (consulted for BYLAYER).
 * @param layerColors Layer colour table (lower-cased keys).
 * @param blockColor  Resolved colour of the parent INSERT (BYBLOCK chain) or null.
 */
export function resolvePlotRgb(
  entityColor: string | undefined,
  layerName: string,
  layerColors: LayerColorMap,
  blockColor: string | null = null,
): RgbColor {
  const hex = resolveColorHex(entityColor, layerName, layerColors, blockColor);
  if (isWhite(hex)) return PLOT_BLACK;
  return hexToRgb(hex);
}
